import React from "react";
import { twMerge } from "tailwind-merge"; // Import the library

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  children: React.ReactNode;
  variant?:
    | "default"
    | "primary"
    | "danger"
    | "dangerDiscrete"
    | "ghost"
    | "link"
    | "hollow"
    | "tab"
    | "group"
    | "highlight";
  size?: "sm" | "default" | "icon" | "lg";
  isActive?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      children,
      variant = "default",
      className = "",
      size = "default",
      isActive = false,
      ...props
    },
    ref,
  ) => {
    const baseStyles =
      "flex items-center gap-2 rounded-md font-semibold transition-all duration-200 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed inline-flex justify-center";

    const variants = {
      default: "bg-neutral-700 text-white hover:bg-neutral-600",
      primary:
        "bg-gradient-to-r from-rose-500 to-amber-600 text-white hover:from-rose-600 hover:to-amber-700",
      danger: "bg-rose-500 text-white hover:bg-rose-600",
      dangerDiscrete: "bg-transparent text-white hover:bg-rose-600",
      ghost: "bg-transparent text-neutral-300 hover:bg-neutral-700/50",
      link: "font-medium bg-transparent text-orange-500 hover:text-orange-600",
      hollow:
        "bg-transparent border border-rose-500 text-rose-500 hover:bg-rose-500/10",
      tab: `relative min-h-10 shrink-0 rounded-none border-0 bg-transparent px-4 py-2 text-sm font-medium transition-colors duration-150 after:absolute after:inset-x-3 after:bottom-0 after:h-0.5 after:rounded-full after:transition-colors focus-visible:z-10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-orange-400/60 ${
        isActive
          ? "bg-neutral-800/60 text-neutral-50 after:bg-orange-400"
          : "text-neutral-400 after:bg-transparent hover:bg-neutral-800/40 hover:text-neutral-100 hover:after:bg-neutral-600"
      }`,
      highlight: `rounded-none font-medium transition-colors duration-200 bg-transparent ${
        isActive
          ? "text-orange-500"
          : "text-neutral-400 hover:text-neutral-100 hover:border-neutral-700"
      }`,
      group: `flex-grow font-medium transition-colors duration-200 ease-in-out focus:outline-none ${
        isActive
          ? "bg-orange-500 text-white hover:bg-orange-600"
          : "bg-neutral-750 text-neutral-300 hover:bg-neutral-600"
      }`,
    };

    const sizes = {
      default: "px-4 py-2 text-sm",
      sm: "px-3 py-1.5 text-xs",
      icon: "p-1.5",
      lg: "px-5 py-3 text-lg",
      tab: "px-4 py-2 text-sm",
    };

    const sizeClass = variant === "tab" ? sizes.tab : sizes[size];

    // Wrap the class strings in twMerge()
    // It accepts multiple strings and merges them correctly.
    const finalClassName = twMerge(
      baseStyles,
      variants[variant],
      sizeClass,
      className, // Your external classes come last, so they take precedence
    );

    return (
      <button
        ref={ref}
        className={finalClassName} // Use the merged class name
        {...props}
      >
        {children}
      </button>
    );
  },
);

Button.displayName = "Button";

export default Button;
